// @flow
import SearchUrlFormatter from './formatters/SearchUrlFormatter';
import type {SearchRequest} from './models/SearchRequest';
import petSyncer from './utils/petSyncer';
import generateUrl from './utils/generateUrl';
import type {SyncWithStrategy} from './utils/petSyncer';

export const ROOT_PATH = '/search';

export type Root = typeof ROOT_PATH;

type Options = {
    petSyncStrategy?: SyncWithStrategy,
    canonicalizeKeywords?: boolean,
    siteContext?: Object
};

/**
 * Generates a /search style search page URL for the given {@link SearchRequest}. By default this function generates
 * SEO compliant relative /search URLs with keyword canonicalization that lowercases the destination keywords, replaces whitespace with dashes, and strips out non-alphanumeric characters
 *
 * @example
 * toURL({
 *  q: 'Austin, TX, USA',
 *  filter: ['filter:38'],
 *  petInclued: true
 * });
 * // returns
 * // '/search/keywords:austin-tx-usa/filter:38?petInclued=true'
 *
 * toURL({
 *  q: 'Austin, TX, USA',
 *  filter: ['filter:38'],
 *  petInclued: true
 * }, {
 *  canonicalizeKeywords: false
 * });
 * // returns
 * // '/search/keywords:Austin%2C%20TX%2C%20USA/filter:38?petInclued=true)
 * @param searchRequest - Search Request model
 * @param {object} [options] - toURL options
 * @param {SyncWithStrategy} [options.petSyncStrategy] - Strategy to use for syncing the pet refinements with petIncluded trip data, defaults to 'both'
 * @param {boolean} [options.canonicalizeKeywords] - Turns off canonical keyword transform and use URI encoding instead
 * @param {object} [options.siteContext] - global config siteContext data.
 * @param {string} [options.siteContext.hostname] - site hostname. If present, the URL will be generated as an absolute URL.
 * @param {string} [options.siteContext.basePath] - site base path
 * @returns {string} with the search page relative URL for the given {@link SearchRequest} object.
 */
export default function toURL(
    searchRequest: SearchRequest,
    options: Options = {}
): string {
    const {siteContext} = options;
    // sync pets
    searchRequest = petSyncer(searchRequest, options.petSyncStrategy);

    const {canonicalizeKeywords} = {canonicalizeKeywords: true, ...options};

    const formattedSearchUrl: string = new SearchUrlFormatter().format(
        searchRequest,
        {canonicalizeKeywords}
    );

    return generateUrl(
        siteContext && siteContext.hostname,
        formattedSearchUrl,
        siteContext && siteContext.basePath
    );
}
